import { API_ORIGIN, isLocalDevHost } from './config.js';

const DEFAULT_ICE_POOL_SIZE = 2;

export class IceConfigError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'IceConfigError';
  }
}

function shouldRequireTurn() {
  if (typeof window === 'undefined') return false;
  return !isLocalDevHost(window.location.hostname);
}

const ICE_GATHERING_TIMEOUT = 5000;
const ICE_CONFIG_TIMEOUT_MS = 2500;
const DEFAULT_ICE_CONFIG_ATTEMPTS = 3;
const DEFAULT_ICE_RETRY_DELAYS_MS = [400, 1200];
const DEFAULT_RELAY_FALLBACK_MS = 5000;

function toJsonCandidate(candidate) {
  if (!candidate) return null;
  if (typeof candidate.toJSON === 'function') return candidate.toJSON();
  const json = {};
  if (candidate.candidate != null) json.candidate = candidate.candidate;
  if (candidate.sdpMid != null) json.sdpMid = candidate.sdpMid;
  if (candidate.sdpMLineIndex != null) json.sdpMLineIndex = candidate.sdpMLineIndex;
  if (candidate.usernameFragment != null) json.usernameFragment = candidate.usernameFragment;
  return json;
}

function toJsonDescription(description) {
  if (!description) return null;
  if (typeof description.toJSON === 'function') {
    const json = description.toJSON();
    return { type: json.type, sdp: json.sdp };
  }
  return { type: description.type, sdp: description.sdp };
}

function hasTurnServer(iceConfig) {
  return (iceConfig?.iceServers || []).some((server) => {
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    return urls.some((url) => String(url).toLowerCase().startsWith('turn'));
  });
}

function waitMs(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export class WebRTCManager {
  constructor({
    relayFallbackMs = DEFAULT_RELAY_FALLBACK_MS,
    requireTurn = shouldRequireTurn(),
    iceConfigAttempts = DEFAULT_ICE_CONFIG_ATTEMPTS,
    iceRetryDelaysMs = DEFAULT_ICE_RETRY_DELAYS_MS,
  } = {}) {
    this.pc = null;
    this.localStream = null;
    this.remoteStream = null;
    this.onRemoteStream = null;
    this.onIceCandidate = null;
    this.onIceRestart = null;
    this.onConnectionState = null;
    this.iceGatheringTimeout = null;
    this.connectWatchdog = null;
    this.pendingIceCandidates = [];
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
    this.iceConfigPromise = null;
    this.requireTurn = requireTurn;
    this.iceConfigAttempts = Math.max(1, iceConfigAttempts);
    this.iceRetryDelaysMs = iceRetryDelaysMs;
    this.relayFallbackMs = relayFallbackMs;
    this.relayFallbackAttempted = false;
    this.forceRelay = false;
    this.role = null;
    this.closed = false;
  }

  async getIceConfig() {
    if (!this.iceConfigPromise) {
      this.iceConfigPromise = this.fetchIceConfigWithRetry().then(
        (config) => config,
        (error) => {
          this.iceConfigPromise = null;
          throw error;
        },
      );
    }

    return this.iceConfigPromise;
  }

  async fetchIceConfigWithRetry() {
    let lastError;
    for (let attempt = 0; attempt < this.iceConfigAttempts; attempt += 1) {
      if (attempt > 0) {
        const delay = this.iceRetryDelaysMs[attempt - 1] ?? this.iceRetryDelaysMs.at(-1) ?? 400;
        await waitMs(delay);
      }
      try {
        return await this.fetchIceConfig();
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  }

  async fetchIceConfig() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ICE_CONFIG_TIMEOUT_MS);

    try {
      const response = await fetch(`${API_ORIGIN}/api/ice-servers`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new IceConfigError(`ICE config request failed with ${response.status}`);
      }

      const config = await response.json();
      const iceServers = Array.isArray(config?.iceServers) ? config.iceServers : [];
      if (iceServers.length === 0) {
        throw new IceConfigError('ICE config did not include servers');
      }
      if (this.requireTurn && !hasTurnServer({ iceServers })) {
        throw new IceConfigError('ICE config did not include TURN');
      }

      return {
        iceServers,
        iceTransportPolicy: config.iceTransportPolicy || 'all',
        iceCandidatePoolSize: config.iceCandidatePoolSize ?? DEFAULT_ICE_POOL_SIZE,
      };
    } catch (error) {
      if (error instanceof IceConfigError) throw error;
      throw new IceConfigError('ICE config could not be loaded', { cause: error });
    } finally {
      clearTimeout(timer);
    }
  }

  hasLiveLocalAudio() {
    return Boolean(this.localStream?.getAudioTracks?.().some((track) => track.readyState === 'live'));
  }

  async initLocalStream() {
    if (this.hasLiveLocalAudio()) return this.localStream;
    if (this.localStreamPromise) return this.localStreamPromise;

    this.localStreamPromise = navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    }).then((stream) => {
      this.localStream = stream;
      console.log('Local stream obtained');
      return stream;
    }).catch((error) => {
      console.error('Failed to get local stream:', error);
      throw error;
    }).finally(() => {
      this.localStreamPromise = null;
    });

    return this.localStreamPromise;
  }

  async createPeerConnection({ forceRelay = false } = {}) {
    if (this.pc) {
      console.log('Closing existing peer connection');
      this.pc.close();
    }

    const iceConfig = await this.getIceConfig();
    this.forceRelay = forceRelay || this.forceRelay;

    this.pc = new RTCPeerConnection({
      iceServers: iceConfig.iceServers,
      iceCandidatePoolSize: iceConfig.iceCandidatePoolSize ?? DEFAULT_ICE_POOL_SIZE,
      iceTransportPolicy: this.forceRelay && hasTurnServer(iceConfig)
        ? 'relay'
        : (iceConfig.iceTransportPolicy || 'all'),
    });
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
    this.closed = false;

    console.log('Peer connection created');

    this.pc.onicecandidate = (event) => {
      if (event.candidate && this.onIceCandidate) {
        console.log('Local ICE candidate generated');
        this.onIceCandidate(toJsonCandidate(event.candidate));
      }
    };

    this.pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        console.log('Remote track received');
        this.remoteStream = event.streams[0];
        if (this.onRemoteStream) {
          this.onRemoteStream(this.remoteStream);
        }
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.pc.iceConnectionState);

      if (this.pc.iceConnectionState === 'connected' || this.pc.iceConnectionState === 'completed') {
        this.clearConnectWatchdog();
        this.onConnectionState?.('connected');
      }

      if (this.pc.iceConnectionState === 'failed') {
        this.handleIceFailure();
      }
    };

    this.pc.onconnectionstatechange = () => {
      console.log('Connection state:', this.pc.connectionState);
      if (this.pc.connectionState === 'connected') {
        this.clearConnectWatchdog();
      }
      this.onConnectionState?.(this.pc.connectionState);
    };

    this.pc.onsignalingstatechange = () => {
      console.log('Signaling state:', this.pc.signalingState);
    };

    this.startConnectWatchdog();
  }

  addLocalTracks() {
    if (!this.pc || !this.localStream) {
      console.warn('Cannot add tracks - PC or stream not ready');
      return;
    }

    if (this.tracksAdded) {
      console.log('Tracks already added, skipping');
      return;
    }

    console.log('Adding local tracks to peer connection');
    this.localStream.getTracks().forEach((track) => {
      console.log('Adding track:', track.kind);
      this.pc.addTrack(track, this.localStream);
    });
    this.tracksAdded = true;
  }

  async createOffer() {
    this.role = 'offerer';
    await this.createPeerConnection({ forceRelay: this.forceRelay });
    this.addLocalTracks();

    try {
      console.log('Creating offer...');
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      console.log('Offer created, SDP length:', this.pc.localDescription.sdp?.length);
      return toJsonDescription(this.pc.localDescription);
    } catch (error) {
      console.error('Failed to create offer:', error);
      throw error;
    }
  }

  async handleOffer(offer) {
    this.role = 'answerer';
    if (this.pc) {
      this.pendingIceCandidates = [];
    }

    await this.createPeerConnection({ forceRelay: this.forceRelay });
    this.addLocalTracks();
    this.isOfferHandled = true;

    try {
      console.log('Handling offer...');
      console.log('Offer type:', offer.type);
      await this.pc.setRemoteDescription(offer);
      this.hasRemoteDescription = true;

      await this.addPendingIceCandidates();

      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      console.log('Answer created, SDP length:', this.pc.localDescription.sdp?.length);
      return toJsonDescription(this.pc.localDescription);
    } catch (error) {
      console.error('Failed to handle offer:', error);
      this.isOfferHandled = false;
      throw error;
    }
  }

  async handleAnswer(answer) {
    if (!this.pc) {
      console.error('No peer connection when handling answer');
      return;
    }

    try {
      console.log('Handling answer...');
      console.log('Answer type:', answer.type);
      await this.pc.setRemoteDescription(answer);
      this.hasRemoteDescription = true;
      this.isAnswerHandled = true;

      await this.addPendingIceCandidates();

      console.log('Answer set as remote description');
    } catch (error) {
      console.error('Failed to handle answer:', error);
      this.isAnswerHandled = false;
      throw error;
    }
  }

  async addIceCandidate(candidate) {
    if (!this.pc) {
      console.log('Queuing ICE candidate (no PC yet)');
      this.pendingIceCandidates.push(candidate);
      return;
    }

    if (!this.hasRemoteDescription) {
      console.log('Queuing ICE candidate (no remote description yet)');
      this.pendingIceCandidates.push(candidate);
      return;
    }

    try {
      await this.pc.addIceCandidate(candidate);
      console.log('ICE candidate added');
    } catch (error) {
      console.error('Failed to add ICE candidate:', error);
    }
  }

  async addPendingIceCandidates() {
    if (this.pendingIceCandidates.length === 0 || !this.pc) return;

    console.log(`Adding ${this.pendingIceCandidates.length} pending ICE candidates`);

    for (const candidate of this.pendingIceCandidates) {
      try {
        await this.pc.addIceCandidate(candidate);
        console.log('Pending ICE candidate added');
      } catch (error) {
        console.error('Failed to add pending ICE candidate:', error);
      }
    }

    this.pendingIceCandidates = [];
  }

  waitForIceGathering() {
    return new Promise((resolve) => {
      if (!this.pc || this.pc.iceGatheringState === 'complete') {
        console.log('ICE gathering already complete or PC closed');
        resolve();
        return;
      }

      const checkState = () => {
        if (this.pc && this.pc.iceGatheringState === 'complete') {
          clearTimeout(this.iceGatheringTimeout);
          this.pc.removeEventListener('icegatheringstatechange', checkState);
          console.log('ICE gathering complete');
          resolve();
        }
      };

      this.pc.addEventListener('icegatheringstatechange', checkState);

      this.iceGatheringTimeout = setTimeout(() => {
        if (this.pc) {
          this.pc.removeEventListener('icegatheringstatechange', checkState);
        }
        console.log('ICE gathering timeout (proceeding anyway)');
        resolve();
      }, ICE_GATHERING_TIMEOUT);
    });
  }

  startConnectWatchdog() {
    this.clearConnectWatchdog();
    if (!this.relayFallbackMs) return;

    this.connectWatchdog = setTimeout(() => {
      this.fallbackToRelay();
    }, this.relayFallbackMs);
  }

  clearConnectWatchdog() {
    if (this.connectWatchdog) {
      clearTimeout(this.connectWatchdog);
      this.connectWatchdog = null;
    }
  }

  async fallbackToRelay() {
    if (this.closed || this.relayFallbackAttempted || !this.pc) return;

    const iceState = this.pc.iceConnectionState;
    const connectionState = this.pc.connectionState;
    if (iceState === 'connected' || iceState === 'completed' || connectionState === 'connected') {
      return;
    }

    let iceConfig;
    try {
      iceConfig = await this.getIceConfig();
    } catch (error) {
      console.error('ICE config unavailable for TURN relay:', error);
      return;
    }
    if (!hasTurnServer(iceConfig)) return;

    this.relayFallbackAttempted = true;
    this.forceRelay = true;
    console.log('ICE still connecting, falling back to TURN relay');

    if (this.role !== 'offerer') return;

    this.pendingIceCandidates = [];
    await this.createPeerConnection({ forceRelay: true });
    this.addLocalTracks();

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.onIceRestart?.(toJsonDescription(this.pc.localDescription));
  }

  async handleIceFailure() {
    console.log('ICE connection failed, attempting restart...');
    await this.fallbackToRelay();

    if (this.relayFallbackAttempted || !this.pc) return;

    try {
      await this.pc.restartIce();

      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);

      if (this.onIceRestart) {
        this.onIceRestart(toJsonDescription(this.pc.localDescription));
      }
    } catch (error) {
      console.error('ICE restart failed:', error);
    }
  }

  async restartIce() {
    if (!this.pc) {
      console.log('No peer connection to restart');
      return;
    }

    console.log('Restarting ICE...');
    await this.pc.restartIce();
  }

  close({ keepLocal = false } = {}) {
    this.closed = true;
    this.clearConnectWatchdog();

    if (!keepLocal && this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.localStream = null;
      this.localStreamPromise = null;
    }

    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    if (this.iceGatheringTimeout) {
      clearTimeout(this.iceGatheringTimeout);
      this.iceGatheringTimeout = null;
    }

    this.remoteStream = null;
    this.pendingIceCandidates = [];
    this.hasRemoteDescription = false;
    this.tracksAdded = false;
    this.isOfferHandled = false;
    this.isAnswerHandled = false;
    this.relayFallbackAttempted = false;
    this.forceRelay = false;
    this.role = null;
  }
}
